<template>
  <StorageLeadForm
    v-if='isSoon || !boxes.length'
    :i18n-key='isSoon ? "is_soon" : "no_available_boxes"'
    :submit-url='leadSubmitUrl'
    :lead-type='leadType'
    :graphql-url='graphqlUrl'
  />
  <template v-else>
    <h2 ref='headlineRef'>
      {{ tp('available_boxes') }} ({{ filteredBoxes.length }})
    </h2>
    <div class='filters'>
      <LabelButton
        v-for='(sizeFilter, index) in sizeFilters'
        :key='index'
        :is-active='sizeFilter.isChecked'
        :is-reset='sizeFilter.isChecked'
        :is-disabled='isFilterDisabled(sizeFilter)'
        @click='syncSizeFilter(sizeFilter, !sizeFilter.isChecked)'
        @reset='syncSizeFilter(sizeFilter, false)'
      >
        <span>
          {{ tp(`sizes.${sizeFilter.size}.title`) }}
          ({{ sizeBoxesCount(sizeFilter.size) }})
        </span>
        <span>
          {{ tp(`sizes.${sizeFilter.size}.subtitle`) }}
        </span>
      </LabelButton>
      <DropdownFilter
        v-if='levelFilter.options.length'
        v-bind='levelFilter'
        :is-force-desktop-style='true'
        @filter:update='syncLevelFilter($event)'
        @filter:reset='resetLevelFilter'
      />
      <div
        v-if='sizeFilters.length'
        ref='guideButtonRef'
        class='guide'
        @click='toggleGuideModal(true)'
      >
        {{ tp('guide') }}
      </div>
    </div>
    <div
      ref='columnsRef'
      class='columns'
    >
      <StorageSchema
        v-if='!isMobile || isMobileMap'
        :current-level-id='currentLevel.id'
        :levels='levels'
        :available-boxes='boxes'
        :filtered-boxes='filteredBoxes'
        :fabric-boxes='fabricBoxes'
        :hovered-box-id='hoveredBoxId'
        @box:click='boxClick'
        @box:mouseover='boxMouseOver'
        @box:mouseout='boxMouseOut'
        @level:select='levelSelect'
      />
      <TransitionGroup
        v-if='!isMobile || !isMobileMap'
        :duration='500'
        tag='div'
        class='size-groups'
      >
        <template v-if='filteredBoxes.length'>
          <div
            v-for='(sizeBoxes, size) in groupedFilteredBoxes'
            :key='size'
            class='size-group'
          >
            <div class='headline'>
              {{ tp(`sizes.${size}.title`) }}
              <span class='size-count'>({{ sizeBoxes.length }})</span>
            </div>
            <div
              class='size-boxes-swiper'
              :class='{ "is-active": sizeBoxes.length > 1 }'
            >
              <TransitionGroup
                :duration='500'
                tag='div'
                class='size-boxes-swiper-wrapper'
              >
                <StorageBox
                  v-for='box in sizeBoxes'
                  :key='box.id'
                  v-bind='box'
                  @box:mouseover='boxMouseOver'
                  @box:mouseout='boxMouseOut'
                />
              </TransitionGroup>
            </div>
          </div>
        </template>
        <StorageLeadForm
          v-else
          i18n-key='no_filtered_boxes'
          :submit-url='leadSubmitUrl'
          :lead-type='leadType'
          :graphql-url='graphqlUrl'
        />
      </TransitionGroup>
    </div>
    <MapSwitcher
      v-if='isMobile && IS_MOBILE_SWITCHER_ENABLED'
      :is-map-active='isMobileMap'
      i18n-key='frontend.storage_schema_boxes.map_switcher'
      @toggle-map='toggleMobileMap'
    />
  </template>

  <Modal
    :is-active='isGuideModal'
    :is-slide-down-handle='true'
    css-class='b-guide-modal'
    @modal:hide='toggleGuideModal(false)'
  >
    <GuideModal
      :sizes='props.guideSizes'
      :available-sizes='availableSies'
      @size:select='handleSizeSelect'
    />
  </Modal>
</template>

<script>
export default {
  name: 'StoragesSchemaBoxes',
  inheritAttrs: false
};
</script>

<script setup>
import Turbolinks from 'turbolinks';
import queryString from 'query-string';
import { debounce } from 'throttle-debounce';
import {
  ref,
  computed,
  watch,
  onBeforeMount,
  onMounted
} from 'vue';

import compact from 'lodash/compact';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import values from 'lodash/values';

import { useMobileDetect } from '../composables/mobile_detect';

import DropdownFilter from '../components/dropdown_filter/index';
import LabelButton from '../components/label_button';
import Modal from '../components/modal';
import GuideModal from './guide_modal';
import StorageSchema from './schema';
import StorageBox from './box';
import StorageLeadForm from './lead_form';
import MapSwitcher from './map_switcher';

import SweetScroll from '@/helpers/sweet_scroll';
import isVisibleInViewport from '@/helpers/is_visible_in_viewport';
import makeVueModal from '@/views/modal/make_vue_modal';
import { t, prepared } from '@/helpers/i18n';

const props = defineProps({
  isSoon: { type: Boolean, required: true },
  boxes: { type: Array, required: true },
  fabricBoxes: { type: Array, required: true },
  levels: { type: Array, required: true },
  guideSizes: { type: Array, required: true },
  leadType: { type: String, required: true },
  leadSubmitUrl: { type: String, required: true },
  graphqlUrl: { type: String, required: true }
});
const tp = prepared('frontend.storage_schema_boxes.index');

const IS_MOBILE_SWITCHER_ENABLED = false;

const hoveredBoxId = ref(null);
const currentLevel = ref(props.levels[0]);
const isGuideModal = ref(false);
const guideButtonRef = ref(null);
const headlineRef = ref(null);

const columnsRef = ref(null);
const swipers = ref(null);
const { isMobile } = useMobileDetect();
const isMobileMap = ref(false);

const sizeFilters = ref(
  props.boxes
    |> reduce(?, (memo, box) => {
      memo[box.size] ||= {
        size: box.size,
        boxesCount: 0,
        isChecked: false
      };
      memo[box.size].boxesCount += 1;
      return memo;
    }, {})
    |> values(?)
);
const availableSies = computed(() => sizeFilters.value.map(({ size }) => size));
const activeSizes = computed(() => (
  sizeFilters.value
    .filter(({ isChecked }) => isChecked)
    .map(({ size }) => size)
));

const levelFilter = ref({
  name: 'level',
  label: tp('level_filter.name'), // Make sure this translation key exists
  isActive: false,
  isNew: false,
  isRadio: false,
  options: props.levels.length > 1 ?
    props.levels.map(level => ({
      value: level.id,
      label: level.number.toString(),
      isChecked: false,
      isDisabled: computed(() => !levelBoxesCount(level.id).length)
    })) :
    [],
  isCheckedByDefault: false,
  isSingleLabel: false,
  isSearch: false
});
const activeLevelFilterIds = computed(() => (
  levelFilter.value.options
    .filter(option => option.isChecked)
    .map(option => option.value)
));

const mappedBoxes = computed(() => (
  props.boxes.map(box => ({
    ...box,
    isHover: box.id === hoveredBoxId.value
  }))
));
const filteredBoxes = computed(() => (
  mappedBoxes.value
    .filter(({ size, levelId, isHover }) => (
      isHover ||
        (!activeSizes.value.length || activeSizes.value.includes(size)) &&
        (!activeLevelFilterIds.value.length || activeLevelFilterIds.value.includes(levelId))
    ))
));
const groupedFilteredBoxes = computed(() => (
  props.boxes
    .map(box => box.size)
    |> uniq
    |> reduce(?, (memo, size) => {
      const boxes = filteredBoxes.value.filter(box => box.size === size);
      if (boxes.length) {
        memo[size] = boxes;
      }
      return memo;
    }, {})
));

function extractLocation() {
  const queryParams = queryString.parse(window.location.href.replace(/.*?(?:\?|$)/, ''));
  setFilters(queryParams);

  const checkedLevelIds = levelFilter.value.options
    .filter(option => option.isChecked)
    .map(option => option.value);

  if (checkedLevelIds.length) {
    currentLevel.value = props.levels.find(({ id }) => id === checkedLevelIds[0]);
  }
}

function setFilters(queryParams) {
  forEach(queryParams, (values, filterName) => {
    values.split(',').forEach(value => {
      if (filterName === 'size') {
        sizeFilters.value.find(({ size }) => size === value)?.isChecked = true;
      } else if (filterName === 'level') {
        const option = levelFilter.value.options.find(option => String(option.value) === value);
        if (option) option.isChecked = true;
      }
    });
  });
}

function syncSizeFilter(filter, isChecked) {
  if (isFilterDisabled(filter)) { return; }

  filter.isChecked = isChecked;
  saveUrlLocation();
}

function syncLevelFilter(attributes) {
  Object.assign(levelFilter.value, attributes);

  if (attributes.isActive === undefined) {
    saveUrlLocation();

    // Logic for switching levels
    const checkedLevelIds = levelFilter.value.options
      .filter(option => option.isChecked)
      .map(option => option.value);

    // Try to switch to the only selected level
    if (checkedLevelIds.length === 1) {
      currentLevel.value = props.levels.find(({ id }) => id === checkedLevelIds[0]);
    }

    // Try to switch to the first of selected levels
    if (checkedLevelIds.length > 1 && !checkedLevelIds.includes(currentLevel.value.id)) {
      currentLevel.value = props.levels.find(({ id }) => id === checkedLevelIds[0]);
    }
  }
}

function resetLevelFilter() {
  levelFilter.value.options.forEach(option => option.isChecked = false);
  levelFilter.value.isActive = false;
  saveUrlLocation();
}

function saveUrlLocation() {
  const mappedFilters = [];

  if (activeSizes.value.length) {
    mappedFilters.push(`size=${activeSizes.value.join(',')}`);
  }

  const checkedLevelIds = levelFilter.value.options
    .filter(option => option.isChecked)
    .map(option => option.value);

  if (checkedLevelIds.length) {
    mappedFilters.push(`level=${checkedLevelIds.join(',')}`);
  }

  const url = location.href.replace(/\?.*/, '') +
    (mappedFilters.length ? '?' : '') +
    mappedFilters.join('&');

  window.history.replaceState({ turbolinks: true, url }, '', url);
}

function levelSelect(levelId) {
  currentLevel.value = props.levels.find(({ id }) => id === levelId);
}

function boxClick(boxId) {
  const box = props.boxes.find(({ id }) => id === boxId);
  Turbolinks.visit(box.url);
}

function boxMouseOver(boxId) {
  hoveredBoxId.value = boxId;
}

function boxMouseOut() {
  hoveredBoxId.value = null;
}

function sizeBoxesCount(size) {
  return props.boxes
    .filter(box => (
      box.size === size && (
        !activeLevelFilterIds.value.length ||
          activeLevelFilterIds.value.includes(box.levelId)
      )
    ))
    .length;
}

function levelBoxesCount(levelId) {
  return props.boxes
    .filter(box => (
      box.levelId === levelId && (
        !activeSizes.value.length || activeSizes.value.includes(box.size)
      )
    ));
}

function isFilterDisabled(sizeFilter) {
  return !sizeFilter.isChecked && !sizeBoxesCount(sizeFilter.size);
}

async function toggleGuideModal(isActive) {
  isGuideModal.value = isActive;
}

async function destroySwipers() {
  if (swipers.value) {
    swipers.value.forEach(swiper => swiper.destroy());
    swipers.value = null;
  }
}

async function rebuildSwipers() {
  destroySwipers();

  if (!isMobile.value || !columnsRef.value) { return; }

  const [{ default: Swiper }, { EffectFade, Pagination }] = await Promise.all([
    import('swiper'),
    import('swiper/modules')
  ]);

  swipers.value = [
    ...columnsRef.value.querySelectorAll('.size-boxes-swiper.is-active')
  ]
    .map(swiperNode => (
      new Swiper(swiperNode, {
        wrapperClass: 'size-boxes-swiper-wrapper',
        slideClass: 'box',
        slidesPerView: 'auto'
      })
    ));
}
const rebuildSwipersDebounced = debounce(500, rebuildSwipers);

async function toggleMobileMap() {
  if (!isMobileMap.value) {
    destroySwipers();
  }

  isMobileMap.value = !isMobileMap.value;

  if (!isMobileMap.value) {
    rebuildSwipers();
  }

  if (!isVisibleInViewport(headlineRef.value, true) ||
    !isVisibleInViewport(columnsRef.value, true)
  ) {
    (await SweetScroll.asyncInstance()).toElement(
      headlineRef.value, { duration: 750, offset: -24 }
    );
  }
}

function handleSizeSelect(size) {
  sizeFilters.value
    .find(sizeFilter => sizeFilter.size === size)
    .isChecked = true;

  isGuideModal.value = false;
}

onBeforeMount(extractLocation);

onMounted(rebuildSwipersDebounced);
watch(filteredBoxes, rebuildSwipersDebounced);
watch(() => filteredBoxes.value.length, () => {
  requestAnimationFrame(() => (
    columnsRef.value
      .querySelector('.size-groups')
      .scrollTo({ top: 0 })
  ));
});
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/functions/scale'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/variables'
@import 'app/packs/stylesheets/mixins/fonts'
@import 'app/packs/stylesheets/mixins/ltr_rtl'
@import 'app/packs/stylesheets/mixins/typography'

h2
  margin-bottom: var(--article-headline-gap)

  +gte_laptop
    text-align: center

.filters
  display: flex

  +lte_ipad
    gap: rem4(12px)
    margin-bottom: rem4(32px)
    flex-wrap: wrap

  +gte_laptop
    gap: 16px
    justify-content: center
    margin-bottom: 40px

.guide
  +p4
  align-items: center
  color: var(--gray-500)
  cursor: pointer
  display: flex
  user-select: none

  +lte_ipad
    flex-basis: 100%
    gap: rem4(8px)
    margin-bottom: rem4(10px)
    margin-top: rem4(-28px)
    order: -1
    padding-bottom: rem4(10px)
    padding-top: rem4(10px)

  +gte_laptop
    gap: 8px
    transition: color var(--transition-duration)

    &:hover
      color: var(--gray-700)

  &:active
    color: var(--gray-700)

  &:after
    +font-icon('\E82C')
    transform: rotate(180deg)

    +lte_ipad
      margin-top: rem4(1px)

    +gte_laptop
      margin-top: 1px

.columns
  display: flex

  +gte_laptop
    gap: 32px

  ::v-deep(.b-storage_map)
    flex-shrink: 0
    width: 36.19%

  .size-groups
    flex-grow: 1

    // +ipad
    //   height: 636px

    +gte_laptop
      overflow-y: scroll
      // offsets for .box box-shadow
      padding: 0 20px
      margin: 0 -20px

    +laptop
      height: #{scale-laptop(636px, 860px)}

    +gte_desktop
      height: 860px

    ::v-deep(.lead-form)
      height: 100%
      justify-content: center

.size-group
  &:not(:last-child)
    +gte_laptop
      margin-bottom: 24px

  .headline
    +p1(700)
    background: #fff

    +gte_laptop
      margin-bottom: 6px
      padding-bottom: 6px
      position: sticky
      top: 0
      z-index: 2

      // offsets for .box box-shadow
      padding-left: 20px
      padding-right: 20px
      margin-left: -20px
      margin-right: -20px

    .size-count
      +p1

// swiper styles
+lte_ipad
  .size-groups
    width: 100%

  .size-boxes-swiper
    display: block
    margin-right: auto
    overflow: hidden
    position: relative

    /* fix box shadow overflow cutting issue */
    margin-left: calc(-1 * var(--container-padding-x))
    margin-right: calc(-1 * var(--container-padding-x))
    padding-bottom: rem4(24px)
    padding-left: var(--container-padding-x)
    padding-right: var(--container-padding-x)
    padding-top: rem4(24px)
    width: calc(100% + 2 * var(--container-padding-x))

  .size-boxes-swiper-wrapper
    display: flex
    position: relative
    width: 100%

    ::v-deep(.box)
      flex-shrink: 0
      margin-right: rem4(16px)
      width: calc(100vw - 2 * var(--container-padding-x) - #{rem4(20px)})

.size-groups,
.size-boxes-swiper-wrapper
  position: relative

/* отключаем анимации бокса, если они находятся внутри анимируемого .size-group */
.size-group.v-move
  .headline
    position: static

  ::v-deep(.box.v-move)
    transition: none

.size-group,
::v-deep(.box)
  &.v-move, /* apply transition to moving elements */
  &.v-enter-active,
  &.v-leave-active
    transition: all 500ms
    width: 100%

  &.v-enter-from,
  &.v-leave-to
    opacity: 0

    +ltr
      transform: translateX(#{rem4(45px)})

    +rtl
      transform: translateX(#{rem4(-45px)})

::v-deep(.box)
  /* ensure leaving items are taken out of layout flow so that moving
    animations can be calculated correctly. */
  &.v-leave-active
    position: absolute

.size-group
  /* ensure leaving items are taken out of layout flow so that moving
    animations can be calculated correctly. */
  &.v-leave-active
    // &:has(+ .size-group:not(.v-leave-active))
    position: absolute
</style>

<style lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/ltr_rtl'
@import 'app/packs/stylesheets/mixins/responsive'

.b-guide-modal > .modal-inner
  +lte_ipad
    padding-left: 0 !important
    padding-right: 0 !important

.size-groups .size-group .box
  &.v-enter-from,
  &.v-leave-to
    opacity: 0

    +ltr
      transform: translateX(#{rem4(45px)})

    +rtl
      transform: translateX(#{rem4(-45px)})
</style>
